import {
  type APIMessage,
  type MessageAbilities,
} from '@alternativamais/chat-core';
import { useModoDesenvolvedor } from '../../../chat/ganchos';
import { useState } from 'react';

import { avisar, avisarErro } from '../../../componentes/Avisos';
import {
  CaixaMenu,
  Flutuante,
  ItemMenu,
  type Posicao,
  SeparadorMenu,
} from '../../../componentes/Flutuante';
import { Icone, type NomeIcone } from '../../../componentes/Icone';
import { abrirCamada } from '../../../chat/camadas';
import { useCliente, useEstado } from '../../../chat/contexto';
import { EMOJIS_RAPIDOS } from '../../../utilidades/emojis';
import { EmojiVisual, lerRecentes } from '../SeletorEmoji';
import { useCanal } from './contexto';

function copiar(texto: string, aviso: string) {
  void navigator.clipboard
    ?.writeText(texto)
    .then(() => avisar(aviso, 'info'))
    .catch(() => avisar('Não deu para copiar.'));
}

/**
 * Barra de ações que aparece ao passar o mouse (C6): reações rápidas,
 * responder, editar e o menu com o resto.
 */
export function Acoes({
  mensagem,
  pode,
}: {
  mensagem: APIMessage;
  pode: MessageAbilities;
}) {
  const dev = useModoDesenvolvedor();
  const cliente = useCliente();
  const canal = useCanal();
  const [menu, setMenu] = useState<Posicao | null>(null);

  const reagir = (emoji: {
    id: string | null;
    name: string;
    animated?: boolean;
  }) => {
    const jaReagi = mensagem.reactions?.some(
      (reacao) =>
        reacao.me &&
        (reacao.emoji.id
          ? reacao.emoji.id === emoji.id
          : reacao.emoji.name === emoji.name),
    );
    const acao = jaReagi
      ? cliente.unreact(mensagem.channel_id, mensagem.id, emoji)
      : cliente.react(mensagem.channel_id, mensagem.id, emoji);
    acao.catch(avisarErro);
  };

  const abrirSeletor = (ancora: Posicao) =>
    canal.abrirEmoji(ancora, (emoji) => reagir(emoji));

  const fechar = () => setMenu(null);
  const link = `${window.location.origin}/canais/${canal.guildId ?? '@me'}/${mensagem.channel_id}/${mensagem.id}`;
  const euId = useEstado((estado) => estado.me?.id);
  const criarThread =
    pode.createThread && canal.criarThread ? canal.criarThread : undefined;
  // Encaminhar: só o que tem conteúdo (como o Discord; sistema não vai).
  const encaminhavel =
    !!mensagem.content ||
    mensagem.attachments.length > 0 ||
    mensagem.embeds.length > 0 ||
    !!mensagem.sticker_items?.length;
  const enqueteAberta =
    !!mensagem.poll &&
    !mensagem.poll.results?.is_finalized &&
    mensagem.author.id === euId &&
    (!mensagem.poll.expiry || new Date(mensagem.poll.expiry) > new Date());
  const fixar = () => {
    const pedido = mensagem.pinned
      ? cliente.api.unpin(mensagem.channel_id, mensagem.id)
      : cliente.api.pin(mensagem.channel_id, mensagem.id);
    pedido
      .then(() =>
        avisar(
          mensagem.pinned ? 'Mensagem desafixada.' : 'Mensagem fixada.',
          'info',
        ),
      )
      .catch(avisarErro);
  };

  return (
    <>
      <div
        role="toolbar"
        aria-label="Ações da mensagem"
        className={`absolute -top-4 right-4 z-[2] items-center gap-0.5 rounded-lg border border-borda bg-elevado p-0.5 shadow-[0_4px_16px_rgba(0,0,0,.35)] group-focus-within:flex group-hover:flex ${
          menu ? 'flex' : 'hidden'
        }`}
      >
        {pode.react &&
          rapidas().map((emoji) => (
            <button
              key={emoji.id ?? emoji.name}
              type="button"
              aria-label={`Reagir com ${emoji.id ? `:${emoji.name}:` : emoji.name}`}
              onClick={() => reagir(emoji)}
              className="flex size-8 items-center justify-center rounded-md text-lg leading-none hover:bg-ativo"
            >
              <EmojiVisual emoji={emoji} tamanho={20} />
            </button>
          ))}
        {pode.react && (
          <BotaoIcone
            rotulo="Adicionar reação"
            icone="sorriso"
            aoClicar={(evento) => {
              const caixa = evento.currentTarget.getBoundingClientRect();
              abrirSeletor({ x: caixa.right, y: caixa.bottom + 4 });
            }}
          />
        )}
        {pode.react && <span className="mx-1 h-5 w-px shrink-0 bg-borda" />}
        {pode.reply && (
          <BotaoIcone
            rotulo="Responder"
            icone="responder"
            aoClicar={() => canal.responder(mensagem)}
          />
        )}
        {pode.edit && (
          <BotaoIcone
            rotulo="Editar"
            icone="editar"
            aoClicar={() => canal.editar(mensagem.id)}
          />
        )}
        {!pode.edit && encaminhavel && (
          <BotaoIcone
            rotulo="Encaminhar"
            icone="encaminhar"
            aoClicar={() => abrirCamada({ tipo: 'encaminhar', mensagem })}
          />
        )}
        {criarThread && (
          <BotaoIcone
            rotulo="Criar thread"
            icone="thread"
            aoClicar={() => criarThread(mensagem)}
          />
        )}
        <BotaoIcone
          rotulo="Mais opções"
          icone="mais-opcoes"
          ativo={!!menu}
          aoClicar={(evento) => {
            const caixa = evento.currentTarget.getBoundingClientRect();
            setMenu(menu ? null : { x: caixa.left - 4, y: caixa.top });
          }}
        />
      </div>
      {menu && (
        <Flutuante
          ancora={menu}
          aoFechar={fechar}
          rotulo="Opções da mensagem"
          papel="menu"
          alinhar="fim"
        >
          <CaixaMenu largura={240}>
            {pode.react && (
              <ItemMenu
                icone="sorriso"
                aoEscolher={() => {
                  fechar();
                  abrirSeletor(menu);
                }}
              >
                Adicionar reação
              </ItemMenu>
            )}
            {!!mensagem.reactions?.length && (
              <ItemMenu
                aoEscolher={() => {
                  fechar();
                  abrirCamada({ tipo: 'reacoes', mensagem });
                }}
              >
                Ver reações
              </ItemMenu>
            )}
            {pode.reply && (
              <ItemMenu
                icone="responder"
                aoEscolher={() => {
                  fechar();
                  canal.responder(mensagem);
                }}
              >
                Responder
              </ItemMenu>
            )}
            {encaminhavel && (
              <ItemMenu
                icone="encaminhar"
                aoEscolher={() => {
                  fechar();
                  abrirCamada({ tipo: 'encaminhar', mensagem });
                }}
              >
                Encaminhar
              </ItemMenu>
            )}
            {criarThread && (
              <ItemMenu
                icone="thread"
                aoEscolher={() => {
                  fechar();
                  criarThread(mensagem);
                }}
              >
                Criar thread
              </ItemMenu>
            )}
            <SeparadorMenu />
            {mensagem.content && (
              <ItemMenu
                aoEscolher={() => {
                  fechar();
                  copiar(mensagem.content, 'Texto copiado.');
                }}
              >
                Copiar texto
              </ItemMenu>
            )}
            <ItemMenu
              aoEscolher={() => {
                fechar();
                cliente
                  .markUnread(mensagem.channel_id, mensagem.id)
                  .catch(avisarErro);
              }}
            >
              Marcar como não lida
            </ItemMenu>
            <ItemMenu
              icone="link"
              aoEscolher={() => {
                fechar();
                copiar(link, 'Link copiado.');
              }}
            >
              Copiar link da mensagem
            </ItemMenu>
            {pode.pin && (
              <ItemMenu
                icone="pin"
                aoEscolher={() => {
                  fechar();
                  fixar();
                }}
              >
                {mensagem.pinned ? 'Desafixar mensagem' : 'Fixar mensagem'}
              </ItemMenu>
            )}
            {enqueteAberta && (
              <ItemMenu
                icone="enquete"
                aoEscolher={() => {
                  fechar();
                  if (
                    !window.confirm(
                      'Encerrar a enquete agora? Ninguém mais vota.',
                    )
                  )
                    return;
                  cliente.api
                    .expirePoll(mensagem.channel_id, mensagem.id)
                    .catch(avisarErro);
                }}
              >
                Encerrar enquete
              </ItemMenu>
            )}
            {(pode.edit || pode.delete) && <SeparadorMenu />}
            {pode.edit && (
              <ItemMenu
                aoEscolher={() => {
                  fechar();
                  canal.editar(mensagem.id);
                }}
              >
                Editar mensagem
              </ItemMenu>
            )}
            {pode.delete && (
              <ItemMenu
                perigo
                icone="lixeira"
                aoEscolher={(evento) => {
                  fechar();
                  canal.apagar(mensagem, evento.shiftKey);
                }}
              >
                Apagar mensagem
              </ItemMenu>
            )}
            <SeparadorMenu />
            {dev && (
              <ItemMenu
                icone="copiar"
                aoEscolher={() => {
                  fechar();
                  copiar(mensagem.id, 'ID copiado.');
                }}
              >
                Copiar ID da mensagem
              </ItemMenu>
            )}
          </CaixaMenu>
        </Flutuante>
      )}
    </>
  );
}

/** Os três da barra: os usados com frequência, completados pelos do design. */
function rapidas(): Array<{
  id: string | null;
  name: string;
  animated?: boolean;
}> {
  const lista = [
    ...lerRecentes(),
    ...EMOJIS_RAPIDOS.map((name) => ({ id: null, name })),
  ];
  const vistos = new Set<string>();
  return lista
    .filter((emoji) => {
      const chave = emoji.id ?? emoji.name;
      if (vistos.has(chave)) return false;
      vistos.add(chave);
      return true;
    })
    .slice(0, 3);
}

function BotaoIcone({
  rotulo,
  icone,
  aoClicar,
  ativo = false,
}: {
  rotulo: string;
  icone: NomeIcone;
  aoClicar: (evento: React.MouseEvent<HTMLButtonElement>) => void;
  ativo?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      title={rotulo}
      onClick={(evento) => {
        // Shift + lixeira na barra não existe; o menu cuida de apagar.
        aoClicar(evento);
      }}
      className={`flex size-8 items-center justify-center rounded-md hover:bg-ativo hover:text-texto ${
        ativo ? 'bg-ativo text-texto' : 'text-texto-2'
      }`}
    >
      <Icone nome={icone} tamanho={20} />
    </button>
  );
}
